import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // If already logged in, redirect immediately
  if (localStorage.getItem("tnt_admin_token")) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/admin/login", { email, password });
      localStorage.setItem("tnt_admin_token", data.token);
      localStorage.setItem("tnt_admin_email", data.email);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-tnt-bg px-5 py-10"
    >
      <div className="w-full max-w-md card-tnt !p-7 sm:!p-9">
        <div className="text-center mb-7">
          <p className="font-display text-tnt-gold text-xs tracking-[0.4em] uppercase mb-3">
            Taps &amp; Tunes Admin
          </p>
          <h1 className="font-display text-3xl sm:text-4xl text-tnt-ink tracking-tight">
            LOG <span className="gold-text">IN</span>
          </h1>
          <p className="mt-3 text-tnt-ink/60 text-sm">
            Manage events, menu photos &amp; gallery.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="font-display tracking-widest text-[11px] uppercase text-tnt-ink/70">
              Email
            </span>
            <div className="mt-1.5 relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tnt-ink/40" />
              <input
                type="email"
                data-testid="admin-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full pl-10 pr-3 py-2.5 bg-white border border-tnt-border rounded-md text-tnt-ink focus:outline-none focus:border-tnt-gold focus:ring-1 focus:ring-tnt-gold"
                placeholder="admin@tapsandtunes.ca"
              />
            </div>
          </label>

          <label className="block">
            <span className="font-display tracking-widest text-[11px] uppercase text-tnt-ink/70">
              Password
            </span>
            <div className="mt-1.5 relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tnt-ink/40" />
              <input
                type="password"
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full pl-10 pr-3 py-2.5 bg-white border border-tnt-border rounded-md text-tnt-ink focus:outline-none focus:border-tnt-gold focus:ring-1 focus:ring-tnt-gold"
                placeholder="••••••••"
              />
            </div>
          </label>

          {error && (
            <p
              data-testid="admin-login-error"
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-gold w-full justify-center mt-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Sign in"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
